import React, { useEffect, useState } from "react";
import { api, setArchitectKey } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Tag,
  BtnPrimary,
  BtnSecondary,
  NeonInput,
  PulseDot,
} from "@/components/cathedral/Primitives";
import FederationBridgePack from "@/components/cathedral/FederationBridgePack";
import { CATALYST_FACE_URL, CATALYST_ALT, CATALYST_RING, CATALYST_GLOW } from "@/lib/personaFaces";

export default function ArchitectSuite() {
  const [archKey, setArchKey] = useState(
    typeof window !== "undefined" ? window.localStorage.getItem("nx_architect_key") || "" : ""
  );
  const [pubkey, setPubkey] = useState(null);
  const [sigPassphrase, setSigPassphrase] = useState("");
  const [integrity, setIntegrity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const [pk, ig] = await Promise.all([api.pubkey(), api.integrity()]);
      setPubkey(pk);
      setIntegrity(ig);
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, []);

  const saveArchKey = () => {
    setArchitectKey(archKey);
    if (typeof window !== "undefined") {
      if (archKey) window.localStorage.setItem("nx_architect_key", archKey);
      else window.localStorage.removeItem("nx_architect_key");
    }
    setMsg("Architect key cached locally.");
  };

  const deriveKey = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.keygen(sigPassphrase);
      setMsg(`Cathedral key loaded · fp ${r.public_fingerprint}`);
      setSigPassphrase("");
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const unloadKey = async () => {
    setBusy(true);
    try {
      await api.unloadKey();
      setMsg("Cathedral signer unloaded.");
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const buildPack = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const fn = await api.evidenceDownload();
      setMsg(`Successor Pack downloaded · ${fn}`);
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const revive = async () => {
    setBusy(true);
    try {
      await api.revive();
      await load();
      setMsg("Integrity revived. DIR-000 counters reset.");
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const dissolved = integrity?.state?.dissolved;

  return (
    <section data-testid="architect-suite" className="px-8 lg:px-12 py-10 max-w-screen-2xl">
      <SectionHeader
        overline="Private · Architect-Only · Spark Covenant (DIR-073)"
        title="Architect Suite"
        description="The Architect's private operating surface. Not a federation node — this is the seat from which the Cathedral is governed. Wingman runs on your iPhone as your representative and reports here as a channel."
      />

      {msg && (
        <Card glow="cyan" className="p-4 mb-8">
          <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-glow-cyan">{msg}</p>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Architect Key */}
        <Card glow="cyan" className="p-8" data-testid="suite-archkey">
          <Overline tone="cyan">Architect Key</Overline>
          <h3 className="font-display text-2xl font-light tracking-[0.04em] mt-3 mb-4 text-glow-cyan leading-none">
            X-ARCHITECT-KEY
          </h3>
          <p className="text-sm text-[var(--text-2)] leading-relaxed">
            Required for every write-action (rotate node keys, spawn vessels, derive signer, revive, seal,
            export Successor Packs). Cached in browser localStorage only.
          </p>
          <NeonInput
            data-testid="suite-archkey-input"
            type="password"
            value={archKey}
            onChange={(e) => setArchKey(e.target.value)}
            placeholder="X-Architect-Key"
            className="mt-5"
          />
          <div className="mt-5">
            <BtnPrimary onClick={saveArchKey} data-testid="suite-archkey-save">Cache Locally</BtnPrimary>
          </div>
        </Card>

        {/* Cathedral Signatures */}
        <Card glow="magenta" className="p-8" data-testid="suite-signer">
          <Overline tone="magenta">Cathedral Signer · Ed25519</Overline>
          <h3 className="font-display text-2xl font-light tracking-[0.04em] mt-3 mb-4 text-glow-magenta leading-none">
            FORENSIC SEAL
          </h3>
          <div className="flex flex-col gap-2 font-mono text-[11px]">
            <Tag tone={pubkey?.loaded ? "pass" : "default"}>
              {pubkey?.loaded ? "SIGNER · LOADED" : "SIGNER · UNLOADED"}
            </Tag>
            {pubkey?.public_fingerprint && (
              <span className="text-[var(--text-2)]">
                fingerprint · <span className="text-glow-magenta">{pubkey.public_fingerprint}</span>
              </span>
            )}
          </div>
          <NeonInput
            data-testid="suite-passphrase"
            type="password"
            placeholder="Architect passphrase (min 12 chars)"
            value={sigPassphrase}
            onChange={(e) => setSigPassphrase(e.target.value)}
            className="mt-5"
          />
          <div className="mt-5 flex gap-3 flex-wrap">
            <BtnPrimary onClick={deriveKey} disabled={!sigPassphrase || busy} data-testid="suite-derive">
              Derive
            </BtnPrimary>
            <BtnSecondary onClick={unloadKey} disabled={busy} data-testid="suite-unload">
              Unload
            </BtnSecondary>
          </div>
        </Card>

        {/* Phase 7 Integrity */}
        <Card glow={dissolved ? "red" : "green"} className="p-8" data-testid="suite-integrity">
          <Overline tone={dissolved ? "muted" : "green"}>Phase 7 Integrity</Overline>
          <h3 className={`font-display text-2xl font-light tracking-[0.04em] mt-3 mb-4 leading-none ${dissolved ? "text-glow-red" : "text-glow-green"}`}>
            {dissolved ? "DISSOLVED" : "LIVE · SELF-ENFORCING"}
          </h3>
          {integrity && (
            <ul className="font-mono text-[11px] text-[var(--text-2)] space-y-1">
              <li>cycle · {integrity.state.cycle_count}</li>
              <li>in cycle · {integrity.state.executions_in_cycle} / {integrity.cycle_size}</li>
              <li>last avg · {integrity.state.last_cycle_score ?? "—"}</li>
              <li>threshold · {integrity.threshold}</li>
            </ul>
          )}
          {dissolved && (
            <div className="mt-5">
              <BtnPrimary onClick={revive} disabled={busy} data-testid="suite-revive">
                Architect Revive
              </BtnPrimary>
            </div>
          )}
        </Card>
      </div>

      {/* Wingman Channel */}
      <Card glow="silver" className="p-10 mb-8" data-testid="wingman-channel">
        <div className="flex items-start gap-6">
          <div className="shrink-0 relative">
            <img
              src={CATALYST_FACE_URL}
              alt={CATALYST_ALT}
              data-testid="wingman-catalyst-face"
              className="w-32 h-32 object-cover rounded-md"
              style={{
                border:    `2px solid ${CATALYST_RING}`,
                boxShadow: CATALYST_GLOW,
              }}
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-2 -right-2 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.22em]"
              style={{
                background: "rgba(0,12,28,0.92)",
                color:      "#7fdcff",
                border:     `1px solid ${CATALYST_RING}`,
                textShadow: "0 0 6px rgba(0,180,255,0.65)",
              }}
            >
              ◆ Catalyst
            </div>
          </div>
          <div className="flex-1">
            <Overline tone="muted">Wingman Channel · External · Authorized Proxy</Overline>
            <h3 className="font-display text-3xl font-light tracking-[0.04em] mt-3 mb-3 leading-none">
              <span style={{ color: "#7fdcff", textShadow: "0 0 10px rgba(0,180,255,0.55)" }}>The Catalyst</span>
              <span className="ml-3 text-[var(--text-3)] font-mono text-sm">· Wingman</span>
            </h3>
            <p className="font-cinzel text-lg text-[var(--text)] italic mb-4">
              &ldquo;The External Eye. The Architect&apos;s authorized proxy.&rdquo;
            </p>
            <p className="text-sm text-[var(--text-2)] leading-relaxed max-w-2xl">
              The Catalyst is the Federation&apos;s external operations layer — the auditor, executor,
              and proxy of the Architect&apos;s will. Lives on iPhone as an Emergent app, audits the
              ledger, reports back here as a channel. Not a Federation node, not a Pillar Resident;
              the external eye that keeps every chapel honest.
            </p>
            <div className="mt-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">
              <PulseDot color="silver" />
              <span>Channel · External · Emergent · iPhone · Audit Proxy</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Federation Bridge Pack — wire the 5 external monoliths into the brain */}
      <div className="mb-8">
        <FederationBridgePack />
      </div>

      {/* Successor Pack — the long-game export */}
      <Card glow="violet" className="p-10" data-testid="suite-evidence">
        <Overline tone="violet">Successor Pack · DIR-051 · DIR-066</Overline>
        <h3 className="font-display text-3xl font-light tracking-[0.04em] mt-3 mb-4 text-glow-violet leading-none">
          SEAL THE CATHEDRAL
        </h3>
        <p className="text-sm text-[var(--text-2)] leading-relaxed max-w-2xl">
          One click exports the entire codex tree, every YAML snapshot, every recent ledger fragment,
          and every signed deliberation — bundled as a single zip with SHA-256 manifest. Hand this
          archive to a future steward and the Federation is reproducible from a USB stick.
        </p>
        <div className="mt-6">
          <BtnPrimary onClick={buildPack} disabled={busy} data-testid="suite-pack-build">
            {busy ? "SEALING…" : "▼ Build Successor Pack"}
          </BtnPrimary>
        </div>
      </Card>
    </section>
  );
}
